/**
 * Which library versions this page load requests, and where that request came
 * from (URL param, tab-scoped sessionStorage, or durable localStorage).
 */

const STORAGE_KEY = 'cad_lib_version_overrides';
const URL_PARAM = 'lib_version';

export type LibName = 'draft-editor' | '3d-lib';
export const SWITCHABLE_LIBS: LibName[] = ['draft-editor', '3d-lib'];

export type OverrideRequests = Partial<Record<LibName, string>>;

export const OVERRIDES_CHANGED_EVENT = 'ergeon:lib-overrides-changed';

const isLibName = (value: string): value is LibName => SWITCHABLE_LIBS.includes(value as LibName);

const isValidRequest = (lib: string, version: unknown): version is string =>
  isLibName(lib) && typeof version === 'string' && version.length > 0;

const sanitizeRequests = (parsed: Record<string, unknown>): OverrideRequests => {
  const result: OverrideRequests = {};
  for (const [lib, version] of Object.entries(parsed)) {
    if (isValidRequest(lib, version)) result[lib as LibName] = version;
  }
  return result;
};

const readFrom = (store: Storage): OverrideRequests => {
  try {
    const raw = store.getItem(STORAGE_KEY);
    return raw ? sanitizeRequests(JSON.parse(raw) as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

const writeTo = (store: Storage, overrides: OverrideRequests): void => {
  try {
    if (Object.keys(overrides).length === 0) store.removeItem(STORAGE_KEY);
    else store.setItem(STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    // storage unavailable — URL param still works for this page load
  }
};

const parseUrlParam = (): OverrideRequests => {
  const param = new URLSearchParams(window.location.search).get(URL_PARAM);
  if (!param) return {};
  const parsed: Record<string, unknown> = {};
  for (const pair of param.split(',')) {
    const [lib, version] = pair.trim().split(':');
    parsed[lib] = version;
  }
  return sanitizeRequests(parsed);
};

let cachedRequests: OverrideRequests | null = null;

/**
 * The overrides requested for this page load, in precedence order:
 *   URL param  >  sessionStorage (tab-scoped)  >  localStorage (durable)
 *
 * The selector persists a chosen override to localStorage — an intentional,
 * durable choice on this browser. A `?lib_version=` link, by contrast, is
 * persisted only to sessionStorage: it survives in-tab navigation/reload but
 * does NOT follow the recipient to future visits. A debug link dropped in Slack
 * shouldn't silently pin whoever clicks it to an old library version forever —
 * that state is invisible once the param drops off the URL. Closing the tab
 * clears it.
 */
export const getOverrideRequests = (): OverrideRequests => {
  if (cachedRequests) return cachedRequests;
  const fromUrl = parseUrlParam();
  if (Object.keys(fromUrl).length > 0) {
    // Layer the param over any existing tab-scoped overrides and re-persist to
    // sessionStorage so in-tab navigation keeps it, without touching localStorage.
    writeTo(sessionStorage, {...readFrom(sessionStorage), ...fromUrl});
  }
  cachedRequests = {...readFrom(localStorage), ...readFrom(sessionStorage), ...fromUrl};
  return cachedRequests;
};

export const hasOverrideRequests = (): boolean => Object.keys(getOverrideRequests()).length > 0;

/**
 * Only the durable (localStorage) overrides — the selector seeds its editable
 * state from these so that hitting Apply doesn't silently promote a tab-scoped
 * `?lib_version=` link into durable storage. A link stays tab-scoped unless the
 * user deliberately changes that lib in the panel.
 */
export const getDurableOverrides = (): OverrideRequests => readFrom(localStorage);

/**
 * Whether `lib` gets overridden on this page load, mirroring buildLoadPlan's
 * rule: any explicitly requested lib, plus draft-editor implicitly whenever
 * only 3d-lib is requested (a 3d-lib override forces DET to load as a bundle
 * too, so it binds to the overridden 3d-lib). The wrapper hooks gate on this
 * rather than raw requests, so the implicit draft-editor bundle is actually
 * rendered — not just downloaded.
 */
export const isOverrideExpected = (lib: LibName): boolean => {
  const requests = getOverrideRequests();
  if (requests[lib]) return true;
  return lib === 'draft-editor' && Boolean(requests['3d-lib']);
};

/**
 * Persist a deliberate selector choice to localStorage (durable across visits)
 * and drop any tab-scoped param override so the two don't fight, then reload
 * without the URL param.
 */
export const setOverrides = (overrides: OverrideRequests): void => {
  writeTo(localStorage, overrides);
  writeTo(sessionStorage, {});
  const url = new URL(window.location.href);
  url.searchParams.delete(URL_PARAM);
  window.location.href = url.toString();
};

export const clearOverrides = (): void => setOverrides({});

export const resetRequestCacheForTests = (): void => {
  cachedRequests = null;
};
